import React from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiDeviation, PapiUser } from '@wix/da-papi-types';
import StandardThumb from '@wix/da-shared-react/pkg/DeviationViews/Thumb/Standard';
import AdoptableShopCard from '@wix/da-shared-react/pkg/ShopCard/AdoptableShopCard';
import ConditionalWrapper from '@wix/da-shared-react/pkg/ConditionalWrapper';
import Slider, { SeeAllSlide } from '@wix/da-shared-react/pkg/Slider';
import { gallectionTypeToResourceType } from '@wix/da-shared-react/pkg/types/gallection';
import { BiEventType } from '@wix/da-bi/pkg/constants';
import { normalizePremiumFolderData } from '@wix/da-shared-react/pkg/utils/normalizePremiumFolderData';
import DuperbrowseContext from '../../../contexts/DuperbrowseContext';
import FolderPaywall from '../_partials/paywalls/FolderPaywall';
import {
  GallectionType,
  GallectionSourceType,
} from '../../../../types/gallection';
import { ExhibitionistPremiumFolderData } from '../../../../types/modules';

import s from './StripExhibitionist.scss';

export interface Props {
  sourceType: GallectionSourceType | GallectionType;
  streamId: string;
  moduleId: number;
  folderId: number;
  deviations: PapiDeviation[];
  seeAllUrl?: string;
  hasMore?: boolean;
  profileOwnerUser: PapiUser;
  premiumFolderData?: ExhibitionistPremiumFolderData;
}

const StripExhibitionist: React.FC<Props> = ({
  folderId,
  sourceType,
  profileOwnerUser,
  premiumFolderData,
  seeAllUrl,
  hasMore,
  deviations,
  streamId,
}) => {
  const { t } = useTranslation();
  const { pointPrice } = normalizePremiumFolderData(premiumFolderData);
  const isCollection = sourceType === GallectionType.COLLECTION;
  const isAdoptables = (
    sourceType === GallectionSourceType.ADOPTABLE_FOR_SALE ||
    sourceType === GallectionSourceType.ADOPTABLE_BOUGHT
  );
  return (
    <ConditionalWrapper
      condition={!isCollection}
      wrap={child => (
        <FolderPaywall
          premiumFolderData={premiumFolderData}
          contentOwner={profileOwnerUser}
          folderId={folderId}
          biData={{
            evid: BiEventType.GALLERY_PURCHASE,
            points: pointPrice,
            typeid: gallectionTypeToResourceType(sourceType),
            itemid: folderId,
            galleryid: folderId,
            authorid: profileOwnerUser.useridUuid,
          }}
        >
          {child}
        </FolderPaywall>
      )}
    >
      <Slider className={c(
        s['slider'],
        isAdoptables && s['slider-adoptables']
      )} scrollBy={0.5}>
        <DuperbrowseContext
          streamId={streamId}
          deviations={deviations}
          parent={{ title: t('duperbrowse.backButton.profile') }}
        >
          {deviations.map(deviation => {
            if (isAdoptables) {
              return (
                <AdoptableShopCard
                  key={deviation.deviationId}
                  deviation={deviation}
                  width={328}
                />
              );
            }
            return (
              <StandardThumb
                key={deviation.deviationId}
                className={s['slider-item']}
                deviation={deviation}
                height={240}
                responsive={false}
              />
            );
          })}
          {hasMore && seeAllUrl && <SeeAllSlide seeAllUrl={seeAllUrl} />}
        </DuperbrowseContext>
      </Slider>
    </ConditionalWrapper>
  );
};
StripExhibitionist.displayName = 'StripExhibitionist';

export default StripExhibitionist;
